import React from 'react'
import { useField } from 'formik'
import Select, {
  SingleValue,
  MultiValue,
  ActionMeta,
  StylesConfig,
} from 'react-select'
import { DropdownWrapper } from './styled'

interface InlineDropdownFieldProps {
  name: string
  placeholder: string
  options: readonly string[]
  searchable?: boolean
  menuMinWidth?: string
  dataTestId?: string
}

type SelectOption = {
  value: string
  label: string
}

const createSelectOptions = (options: readonly string[]): SelectOption[] =>
  options.map((option) => ({ value: option, label: option }))

const inlineSelectStyles: StylesConfig<SelectOption> = {
  container: (base) => ({
    ...base,
    display: 'inline-block',
    verticalAlign: 'baseline',
    margin: '0 2px',
    minWidth: '60px',
    position: 'relative',
    fontSize: 'inherit',
  }),
  control: (base, { hasValue, isFocused }) => ({
    ...base,
    minHeight: 'auto',
    height: 'auto',
    border: 'none',
    borderBottom: `1px solid ${isFocused ? '#3498db' : '#E0E0E0'}`,
    borderRadius: 0,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: '2px 0',
    '&:hover': {
      borderBottom: '1px solid #3498db',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0',
    minHeight: 'auto',
  }),
  singleValue: (base, { hasValue }) => ({
    ...base,
    margin: 0,
    color: hasValue ? 'inherit' : '#a4b0be',
    position: 'static',
    transform: 'none',
    maxWidth: 'none',
  }),
  placeholder: (base) => ({
    ...base,
    margin: 0,
    color: '#a4b0be',
    position: 'static',
    transform: 'none',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: 'inherit',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: 0,
    height: 'auto',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 0 0 4px',
    color: '#a4b0be',
    width: '16px',
    '&:hover': {
      color: '#3498db',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (base) => ({
    ...base,
    marginTop: '8px',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e0e0',
    zIndex: 1000,
    minWidth: '260px',
  }),
  menuList: (base) => ({
    ...base,
    padding: 0,
    maxHeight: '250px',
    borderRadius: '8px',
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    padding: '10px 12px',
    backgroundColor: isSelected
      ? '#EBF5FB'
      : isFocused
      ? '#f5f7fa'
      : 'transparent',
    color: isSelected ? '#3498DB' : 'inherit',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:active': {
      backgroundColor: '#EBF5FB',
    },
  }),
}

const CustomDropdownIndicator = (props: any) => (
  <div
    {...props.innerProps}
    style={{ padding: '0 0 0 4px', color: '#a4b0be', cursor: 'pointer' }}
  >
    <svg
      width='12'
      height='12'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
    >
      <polyline points='6 9 12 15 18 9'></polyline>
    </svg>
  </div>
)
CustomDropdownIndicator.displayName = 'CustomDropdownIndicator'

export const InlineDropdownField: React.FC<InlineDropdownFieldProps> = ({
  name,
  placeholder,
  options,
  searchable = false,
  menuMinWidth,
  dataTestId,
}) => {
  const [field, meta, helpers] = useField(name)

  const hasError = meta.touched && meta.error
  const selectOptions = createSelectOptions(options)
  const selectedOption = selectOptions.find(
    (option) => option.value === field.value
  )

  const dynamicStyles: StylesConfig<SelectOption> = {
    ...inlineSelectStyles,
    control: (base, state) => {
      const baseControlStyles = inlineSelectStyles.control
        ? inlineSelectStyles.control(base, state)
        : base
      return {
        ...baseControlStyles,
        borderBottom: `1px solid ${
          hasError ? '#E74C3C' : state.isFocused ? '#3498db' : '#E0E0E0'
        }`,
      }
    },
    menu: (base) => ({
      ...base,
      minWidth: menuMinWidth || '260px',
    }),
  }

  const customComponents = {
    DropdownIndicator: CustomDropdownIndicator,
  }

  return (
    <DropdownWrapper data-testid={dataTestId || name}>
      <Select
        value={selectedOption || null}
        onChange={(
          newValue: SingleValue<SelectOption> | MultiValue<SelectOption>,
          actionMeta: ActionMeta<SelectOption>
        ) => {
          const selectedOption = newValue as SingleValue<SelectOption>
          if (selectedOption) {
            helpers.setValue(selectedOption.value)
            helpers.setTouched(true)
          }
        }}
        onBlur={() => helpers.setTouched(true)}
        options={selectOptions}
        placeholder={placeholder}
        isSearchable={searchable}
        isClearable={false}
        styles={dynamicStyles}
        components={customComponents}
        menuPlacement='auto'
        menuPosition='absolute'
        blurInputOnSelect
        closeMenuOnSelect
        noOptionsMessage={() => 'No options found'}
      />
    </DropdownWrapper>
  )
}
